import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime, responseStyle } from "@/lib/pgt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, MessageCircle, Reply, StickyNote, GitBranch, IndianRupee, Pencil } from "lucide-react";
import { toast } from "sonner";

const TYPE = {
  outbound: { icon: MessageCircle, ring: "bg-forest text-ivory", label: "Sales team" },
  response: { icon: Reply, ring: "bg-blue-600 text-white", label: "Customer" },
  note: { icon: StickyNote, ring: "bg-amber-500 text-white", label: "Internal note" },
  stage: { icon: GitBranch, ring: "bg-purple-600 text-white", label: "Stage change" },
};

const ORDER_STATUSES = ["order_confirmed", "order_expected"];

export default function TimelinePanel({ customerId }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(null);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["timeline", customerId],
    queryFn: async () => (await api.get(`/customers/${customerId}/timeline`)).data,
  });

  const afterSave = () => {
    setEdit(null);
    ["timeline", "customer", "customers", "dashboard", "reports", "leaderboard", "goals", "revenue-timeseries"]
      .forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  if (isLoading)
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;

  if (events.length === 0)
    return <div className="rounded-xl border border-dashed border-stone-300 bg-white/50 py-14 text-center text-sm text-stone-400">No activity yet. Generate and confirm a message to start the timeline.</div>;

  return (
    <div className="relative pl-2" data-testid="communication-timeline">
      <div className="absolute left-[19px] top-2 bottom-2 w-px bg-stone-200" />
      <div className="space-y-4">
        {events.map((e, i) => {
          const t = TYPE[e.type] || TYPE.note;
          const isOrder = e.type === "response" && ORDER_STATUSES.includes(e.status);
          return (
            <div key={i} className="relative flex gap-3">
              <div className={`z-10 grid h-9 w-9 shrink-0 place-items-center rounded-full ${t.ring} ring-4 ring-ivory`}>
                <t.icon className="h-4 w-4" />
              </div>
              <div className={`flex-1 rounded-xl border bg-white p-3 ${e.type === "outbound" ? "border-forest/20" : e.type === "response" ? "border-blue-200" : "border-stone-200"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-stone-800">{e.title}</span>
                  <span className="text-[10px] text-stone-400 shrink-0">{fmtDateTime(e.at)}</span>
                </div>
                {e.text && (
                  <p className={`mt-1.5 text-sm leading-relaxed whitespace-pre-wrap ${e.type === "outbound" ? "rounded-lg bg-whatsapp-bg/40 p-2 text-stone-700" : "text-stone-600"}`}>
                    {e.text}
                  </p>
                )}
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-stone-400">
                  {e.user_name && <span>{e.user_name}</span>}
                  {e.type === "outbound" && e.next_action && <span>· Next: {e.next_action}</span>}
                  {e.type === "response" && e.status && (
                    <span className={`rounded-full border px-1.5 py-0.5 text-[10px] ${responseStyle(e.status)}`}>{e.status}</span>
                  )}
                  {isOrder && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-semibold text-forest" data-testid={`order-value-${e.response_id}`}>
                      <IndianRupee className="h-3 w-3" />
                      {e.order_value != null ? Number(e.order_value).toLocaleString("en-IN") : "add value"}
                      <button
                        onClick={() => setEdit({ id: e.response_id, value: e.order_value ?? "" })}
                        className="ml-0.5 text-stone-400 transition-colors hover:text-forest"
                        title="Edit order value"
                        data-testid={`edit-order-${e.response_id}`}
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <EditOrderDialog edit={edit} onClose={() => setEdit(null)} onSaved={afterSave} />
    </div>
  );
}

function EditOrderDialog({ edit, onClose, onSaved }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (edit) setValue(edit.value === "" || edit.value == null ? "" : String(edit.value));
  }, [edit]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/responses/${edit.id}`, { order_value: Number(value || 0) });
      toast.success("Order value updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!edit} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-sm" data-testid="edit-order-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><IndianRupee className="h-4 w-4 text-gold" /> Edit order value</DialogTitle>
          <DialogDescription>Correct the recorded sales value. This updates revenue goals and the leaderboard.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>Order value (₹)</Label>
          <Input
            type="number"
            min="0"
            step="1"
            autoFocus
            value={value}
            onChange={(ev) => setValue(ev.target.value)}
            placeholder="e.g. 45000"
            data-testid="edit-order-input"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={save} disabled={saving} data-testid="edit-order-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save value"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
